import { Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
    DragDropContext,
    DragStart,
    DropResult,
    ResponderProvided
} from "react-beautiful-dnd";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { ISessionState } from "../../../../models/session/sessionState";
import { GridRow } from "../../../framework/Grid/GridRow";
import { FormDesignerBuilder } from "./FormDesignerBuilder";
import { componentDefaultValues, FormDesignerComponentPicker } from "./FormDesignerComponentPicker";
import { FormDesignerComponentProperties } from "./FormDesignerComponentProperties";
import { ILineColumnWidth } from "./FormDesignerComponentProperties/FormDesignerComponentPropertiesRow";
import { TaskRepetitionType } from "ui/Scripts/source/models/enums";
import { calculateEndIndex, calculateRows, calculateStartIndex, FormDesignBuilderDroppableId, FormDesignerDataRow, getWebWidthFromClass, IFormDesignerDragging, ITaskTypeFieldPropUpdate } from "../FormDesignerTypes";

interface ITaskTypeDialogFormDesignerProps {
    companyId: string;
    taskRepetitionType: TaskRepetitionType;
    taskType: ITaskEditTypeItem;
    headerDataRow?: ITaskDataRow;
    formDataRow?: ITaskDataRow;
    lineDataRow?: ITaskDataRow;
    lineColumnWidths: ILineColumnWidth[];
    headerTypeCol: ITaskTypeCol;
    formTypeCol: ITaskTypeCol;
    lineTypeCol: ITaskTypeCol;
    sessionState: ISessionState;
    onHeaderSchemaReorder: (startIndex: number, endIndex: number) => void;
    onHeaderSchemaAddItem: (fieldType: string, index: number) => void;
    onFormSchemaReorder: (startIndex: number, endIndex: number) => void;
    onFormSchemaAddItem: (fieldType: string, index: number) => void;
    onRowSchemaReorder: (startIndex: number, endIndex: number) => void;
    onHeaderItemClick: (id: string) => void;
    onFormItemClick: (id: string) => void;
    onTaskTypeHeaderPropUpdate: (values: ITaskTypeFieldPropUpdate[]) => void;
    onTaskTypeFormPropUpdate: (values: ITaskTypeFieldPropUpdate[]) => void;
    onTaskTypeLinePropUpdate: (values: ITaskTypeFieldPropUpdate[]) => void;
    onHeaderTaskDataRowValueUpdate: (id: string, name: string, value: string) => void;
    onFormTaskDataRowValueUpdate: (id: string, name: string, value: string) => void;
    onHeaderRemoveComponent: (id: string) => void;
    onHeaderCopyComponent: (id: string) => void;
    onFormRemoveComponent: (id: string) => void;
    onFormCopyComponent: (id: string) => void;
    onAddFieldToLine: (id: string) => Promise<void>;
    onRemoveFieldFromLine: (id: string) => void;
    onChangeLineColumnWidth: (id: string, classes: string) => void;
    onViewSchema?: () => void;
}

interface ITaskTypeDialogFormDesignerState {
    dragging?: IFormDesignerDragging,
    headerDesignerRows?: FormDesignerDataRow[];
    formDesignerRows?: FormDesignerDataRow[];
}
                
export const TaskTypeDialogFormDesigner = (props: ITaskTypeDialogFormDesignerProps) => {
    const [state, setState] = useState<ITaskTypeDialogFormDesignerState>();

    useEffect(() => {
        setState((old) => ({
            ...old,
            headerDesignerRows: calculateRows(props.taskType?.headerCols, FormDesignBuilderDroppableId.Header)
        }));
    }, [props.taskType?.headerCols]);

    useEffect(() => {
        setState((old) => ({
            ...old,
            formDesignerRows: calculateRows(props.taskType?.formCols, FormDesignBuilderDroppableId.Form)
        }));
    }, [props.taskType?.formCols]);
    
    const handleDragEnd = (result: DropResult, provided: ResponderProvided) => {
        //console.log("handleDragEnd S", result?.destination, result?.source, `'${result?.draggableId}'`);
        if (!result?.destination) return;
        if (result?.source?.droppableId === FormDesignBuilderDroppableId.Picker) {
            setTimeout(() => {
                if ((result?.destination.droppableId ?? "").startsWith(FormDesignBuilderDroppableId.Header)) {
                    const startIndex = calculateStartIndex(props.taskType.headerCols, result.draggableId);
                    const endIndex = calculateEndIndex(state.headerDesignerRows, result.source.droppableId, result.destination.droppableId, result.destination.index, startIndex);
                    props.onHeaderSchemaAddItem(result.draggableId, endIndex);
                } else if ((result?.destination.droppableId ?? "").startsWith(FormDesignBuilderDroppableId.Form)) {
                    const startIndex = calculateStartIndex(props.taskType.formCols, result.draggableId);
                    const endIndex = calculateEndIndex(state.formDesignerRows, result.source.droppableId, result.destination.droppableId, result.destination.index, startIndex);
                    props.onFormSchemaAddItem(result.draggableId, endIndex);
                }
            }, 10);
        } else if (result?.source?.droppableId === FormDesignBuilderDroppableId.Row) {
            props.onRowSchemaReorder(result.source?.index, result.destination?.index);
        } else if ((result?.source?.droppableId ?? "").startsWith(FormDesignBuilderDroppableId.Form)) {
            const field = result.draggableId?.substring(FormDesignBuilderDroppableId.Form.length) ?? "";
            const startIndex = calculateStartIndex(props.taskType.formCols, field);
            const endIndex = calculateEndIndex(state.formDesignerRows, result.source.droppableId, result.destination.droppableId, result.destination.index, startIndex);
            props.onFormSchemaReorder(startIndex, endIndex);
        } else if ((result?.source?.droppableId ?? "").startsWith(FormDesignBuilderDroppableId.Header)) {
            const field = result.draggableId?.substring(FormDesignBuilderDroppableId.Header.length) ?? "";
            const startIndex = calculateStartIndex(props.taskType.headerCols, field);
            const endIndex = calculateEndIndex(state.headerDesignerRows, result.source.droppableId, result.destination.droppableId, result.destination.index, startIndex);
            props.onHeaderSchemaReorder(startIndex, endIndex);
        }
        setState((old) => ({
            ...old,
            dragging: null,
        }));
    };

    const handleDragStart = (initial: DragStart) => {
        //console.log("handleDragStart S", initial, initial.source, initial.source?.droppableId, initial.draggableId, props.taskType);
        if (!initial.source || !initial.draggableId || !props.taskType) return;
        const droppableId = initial.source.droppableId;
        let width = 6;
        if (droppableId === FormDesignBuilderDroppableId.Picker) {
            width = getWebWidthFromClass(componentDefaultValues[initial.draggableId]?.defaultProps?.class) || 6;
        } else {
            let taskTypeCols: ITaskTypeCol[] = [];
            let field = "";
            if (droppableId.startsWith(FormDesignBuilderDroppableId.Header)) {
                taskTypeCols = props.taskType.headerCols;
                field = initial.draggableId.substring(FormDesignBuilderDroppableId.Header.length) ?? "";
            } else if (droppableId.startsWith(FormDesignBuilderDroppableId.Form)) {
                taskTypeCols = props.taskType.formCols;
                field = initial.draggableId.substring(FormDesignBuilderDroppableId.Form.length) ?? "";
            } else if (droppableId.startsWith(FormDesignBuilderDroppableId.Row)) {
                taskTypeCols = props.taskType.lineCols;
                field = initial.draggableId.substring(FormDesignBuilderDroppableId.Row.length) ?? "";
            } else {
                return;
            }
            const taskTypeCol = taskTypeCols.find(item => item.field === field);
            if (!taskTypeCol) return;
            width = getWebWidthFromClass(taskTypeCol.class);
        }
        //console.log("handleDragStart M", width, droppableId);
        setState((old) => ({
            ...old,
            dragging: {
                width,
                source: droppableId,
            },
        }));
    };

    return (
        <GridRow sx={{ flexGrow: 1 }}>
            <DragDropContext
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
            >
                <Grid item sm={3}>
                    <FormDesignerComponentPicker />
                </Grid>
                <Grid item sm={6}>
                    <FormDesignerBuilder
                        taskType={props.taskType}
                        headerDesignerRows={state?.headerDesignerRows}
                        formDesignerRows={state?.formDesignerRows}
                        headerDataRow={props.headerDataRow}
                        formDataRow={props.formDataRow}
                        lineDataRow={props.lineDataRow}
                        headerTypeCol={props.headerTypeCol}
                        formTypeCol={props.formTypeCol}
                        lineTypeCol={props.lineTypeCol}
                        sessionState={props.sessionState}
                        draggingWidth={state?.dragging?.width}
                        draggingSource={state?.dragging?.source}
                        onHeaderItemClick={props.onHeaderItemClick}
                        onFormItemClick={props.onFormItemClick}
                        onHeaderRemoveComponent={props.onHeaderRemoveComponent}
                        onHeaderCopyComponent={props.onHeaderCopyComponent}
                        onFormRemoveComponent={props.onFormRemoveComponent}
                        onFormCopyComponent={props.onFormCopyComponent}
                        onViewSchema={props.onViewSchema}
                        onAddFieldToLine={props.onAddFieldToLine}
                        onRemoveFieldFromLine={props.onRemoveFieldFromLine}
                    />
                </Grid>
            </DragDropContext>
            <Grid item sm={3}>
                <FormDesignerComponentProperties
                    companyId={props.companyId}
                    taskRepetitionType={props.taskRepetitionType}
                    headerCols={props.taskType.headerCols}
                    formCols={props.taskType.formCols}
                    lineCols={props.taskType.lineCols}
                    headerTypeCol={props.headerTypeCol}
                    formTypeCol={props.formTypeCol}
                    lineTypeCol={props.lineTypeCol}
                    lineColumnWidths={props.lineColumnWidths}
                    formDataCol={props.formDataRow && props.formTypeCol
                        ? props.formDataRow.cols.find(c => c.field === props.formTypeCol.field)
                        : null}
                    onHeaderItemClick={props.onHeaderItemClick}
                    onFormItemClick={props.onFormItemClick}
                    onTaskTypeHeaderPropUpdate={props.onTaskTypeHeaderPropUpdate}
                    onTaskTypeFormPropUpdate={props.onTaskTypeFormPropUpdate}
                    onTaskTypeLinePropUpdate={props.onTaskTypeLinePropUpdate}
                    onHeaderTaskDataRowValueUpdate={props.onHeaderTaskDataRowValueUpdate}
                    onFormTaskDataRowValueUpdate={props.onFormTaskDataRowValueUpdate}
                    onAddFieldToLine={props.onAddFieldToLine}
                    onRemoveFieldFromLine={props.onRemoveFieldFromLine}
                    onChangeLineColumnWidth={props.onChangeLineColumnWidth}
                />
            </Grid>
        </GridRow>
    );
};